import { useEffect, useContext, useState } from 'react';
import { AuthContext } from '../../authContext';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { msalInstance } from '../../msalConfig';
import './Login.css';
import AppVersion from '../../components/AppVersion';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPostLoginDestination } from '../../utils/postLoginRedirect';
import { authenticateWithMicrosoft, isUnauthorizedError } from '../../utils/microsoftLogin';

const RedirectHandler = () => {
    const {
        setAssistantList, count, setDate, setSessionId, setUser, setEmail, setAccessToken, setLevel, setIsManager, setAccess, setUserEmail, setUserName, setDomainsAccess, setPopupStatus, setUpdateId, isLogin, setIsLogin, setTermsAccepted
    } = useContext(AuthContext);
    const { t: tLogin } = useTranslation('login');
    const [isAnimating, setIsAnimating] = useState(true);
    const location = useLocation();
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [microsoftError, setMicrosoftError] = useState(location.state?.error || '')
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [setPopStatus, popstatus] = useState('');

    // Função auxiliar para decidir entre Popup ou Redirect baseado no contexto (Iframe ou não)
    const performLogin = async (loginRequest) => {
        const isInIframe = window.self !== window.top;
        
        try {
            if (isInIframe) {
                console.log("Detectado ambiente em iframe, utilizando loginPopup.");
                await msalInstance.loginPopup(loginRequest);
            } else {
                console.log("Ambiente normal, utilizando loginRedirect.");
                await msalInstance.loginRedirect(loginRequest);
            }
        } catch (error) {
            if (error.errorCode === "redirect_in_iframe" || error.errorMessage?.includes("iframed")) {
                await msalInstance.loginPopup(loginRequest);
            } else {
                throw error;
            }
        }
    };

    useEffect(() => {
        const handleRedirectResponse = async () => {
            try {
                const response = await msalInstance.handleRedirectPromise();
                let account = response?.account || msalInstance.getActiveAccount();
                
                if (!account) {
                    
                    try {
                        const ssoResponse = await msalInstance.ssoSilent({
                            scopes: ["User.Read"]
                        });
                        account = ssoResponse.account;
                        msalInstance.setActiveAccount(account);
                    } catch (ssoError) {
                        // Substituído loginRedirect direto pela função 
                        await performLogin({
                            scopes: ["User.Read"]
                        });
                        return;
                    }
                }

                if (account) {
                    msalInstance.setActiveAccount(account);
                    const msalEmail = account.username;
                    setUserEmail(msalEmail);

                    const emailDomain = msalEmail.split('@')[1];
                    const domainsResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_domains`);
                    const domainData = await domainsResponse.json();
                    const allowedDomains = domainData.map(domain => domain.dominio);

                    // Fluxo "sem acesso" (reaproveitado pela recusa do backend — D12)
                    const handleUnauthorized = () => {
                        sessionStorage.removeItem("invalid_domain");
                        console.error('Domínio não autorizado para login.');
                        setIsUnauthorized(true);
                        msalInstance.setActiveAccount(null);

                        setTimeout(async () => {
                            // Substituído loginRedirect direto pela função inteligente
                            await performLogin({
                                scopes: ["User.Read"],
                                prompt: "select_account",
                            });
                        }, 3000);
                    };

                    // Checagem de domínio no cliente: só UX. A decisão válida é a do
                    // servidor, em /auth/microsoft (specs/002-microsoft-auth-idtoken).
                    if (!allowedDomains.includes(emailDomain)) {
                        handleUnauthorized();
                        return;
                    }

                    sessionStorage.removeItem("invalid_domain");

                    // LOGIN BACKEND — idToken do MSAL validado no servidor; sem segredo no cliente.
                    let loginData;
                    try {
                        loginData = await authenticateWithMicrosoft(instance, account);
                    } catch (authError) {
                        if (isUnauthorizedError(authError)) {
                            handleUnauthorized();
                            return;
                        }
                        throw authError;
                    }

                    if (loginData.access_token) {
                        setUser(msalEmail);
                        setEmail(loginData.email);
                        setLevel(loginData.role);
                        setIsManager(Boolean(loginData.is_manager));
                        setAccess(loginData.access);
                        setDomainsAccess(loginData.domains_access || []);
                        setAccessToken(loginData.access_token);
                        setPopupStatus(loginData.popup_info.popup_status); 
                        setUpdateId(loginData.popup_info.update_id);
                        setTermsAccepted(Boolean(loginData.terms_info?.term_status));
                        
                        if (loginData.popup_info.popup_status !== undefined) { 
                            setPopupStatus(loginData.popup_info.popup_status); 
                        } 
                        
                        setIsLogin(true) 

                        try {
                            const graphResponse = await instance.acquireTokenSilent({
                                scopes: ["User.Read"],
                                account: account,
                            });

                            const userGraphResponse = await fetch("https://graph.microsoft.com/v1.0/me", {
                                headers: { Authorization: `Bearer ${graphResponse.accessToken}` }
                            });

                            const userData = await userGraphResponse.json();
                            setUserName(userData.displayName);
                        } catch (graphError) {
                            console.error("Erro ao obter nome da Microsoft Graph:", graphError);
                            setUserName(""); 
                        }

                        const currentDate = new Date();
                        const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}:${String(currentDate.getSeconds()).padStart(2, '0')}.${String(currentDate.getMilliseconds()).padStart(6, '0')}`;
                        setDate(formattedDate);
                        setSessionId(`${msalEmail}-${count}-${formattedDate}`);
                        setAssistantList(loginData.assistants || []);
                        const destination = getPostLoginDestination(loginData.assistants, loginData.role);
                        navigate(destination.path, { state: destination.state });
                    } else {
                        console.error("Erro na autenticação Microsoft");
                    }

                } else {
                    const currentAccount = msalInstance.getActiveAccount();
                    if (currentAccount) {
                        navigate('/hub');
                    } else {
                        // Substituído loginRedirect direto pela função 
                        await performLogin({
                            scopes: ["User.Read"],
                            prompt: 'select_account'
                        });
                        navigate('/unauthorized');
                    }
                }
            } catch (error) {
                console.error("Erro ao tratar login redirect:", error);
            }
        };

        handleRedirectResponse();
    }, [instance, navigate]);

    return (
        <div className={isUnauthorized ? 'unauthorized-wrapper' : 'normal-wrapper'}>
            {isUnauthorized ? (
                <div className="error-container">
                    <img src='imagens/iris.svg' alt="Error" className="error-image" />
                    <h1 className="error-title">{tLogin('mensagem-erro')}</h1>
                    <p className="error-message">{tLogin('domain')}</p>
                </div>
            ) : (
                <div className={`login-container ${isAnimating ? 'login-success' : ''}`}>
                    <div className="image-carousel">
                        <img src="/imagens/linhas.svg" className="background-svg" alt="Background Lines" />
                        <img src="/imagens/logo-e-titulo-tpf.svg" className="logo-svg" alt="Logo TPF" />
                    </div>
                    <AppVersion />
                </div>
            )}
        </div>
    );
};

export default RedirectHandler;